import { Fragment, useMemo, useState } from "react";
import { Button, Icons } from "@/components/ui";
import { menuItemVariants } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/cn";
import { useT } from "@/i18n";
import { inputBarPickersMessages } from "@/i18n/messages/input-bar-pickers";
import { latestModelOptions } from "@/lib/model-picker-options";
import {
  opencodePlanBillingHint,
  type ModelOption,
} from "@/lib/model-registry";
import {
  MODEL_PICKER_CONTROL_HEIGHT,
  MODEL_PICKER_CONTROL_SHAPE,
} from "./model-picker-popover-helpers";

interface Props {
  models: ModelOption[];
  search: string;
  selectedModel?: string;
  planGrouped: boolean;
  saving: boolean;
  unavailable: boolean;
  onSelect: (model: string) => void;
}

export function ModelPickerList({
  models,
  search,
  selectedModel,
  planGrouped,
  saving,
  unavailable,
  onSelect,
}: Props) {
  const t = useT(inputBarPickersMessages);
  const [expanded, setExpanded] = useState(false);
  const latest = useMemo(() => latestModelOptions(models), [models]);
  const query = search.trim().toLowerCase();
  const visible = useMemo(() => {
    if (query) {
      return models.filter(
        (model) =>
          model.label.toLowerCase().includes(query) ||
          model.value.toLowerCase().includes(query),
      );
    }
    return expanded ? models : latest;
  }, [models, latest, query, expanded]);
  const showMore = !expanded && !query && latest.length < models.length;

  return (
    <ul
      key={query}
      className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain py-appkit-2xs [scrollbar-gutter:stable]"
      data-testid="model-picker-model-list"
    >
      {visible.map((model, index) => {
        const selected = model.value === selectedModel;
        const planHeader =
          planGrouped &&
          model.group &&
          model.group !== visible[index - 1]?.group
            ? model.group
            : null;
        return (
          <Fragment key={model.value}>
            {planHeader && (
              <li
                role="presentation"
                className="flex items-baseline gap-appkit-2xs px-appkit-sm pt-appkit-xs pb-0.5"
                data-testid={`model-plan-header-${model.provider ?? model.group}`}
              >
                <span className="text-appkit-mini font-semibold uppercase tracking-wide text-muted">
                  {planHeader}
                </span>
                {opencodePlanBillingHint(model.provider) && (
                  <span className="text-appkit-mini text-subtle">
                    {opencodePlanBillingHint(model.provider)}
                  </span>
                )}
              </li>
            )}
            <li>
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={() => onSelect(model.value)}
                disabled={saving || unavailable}
                className={cn(
                  menuItemVariants(),
                  MODEL_PICKER_CONTROL_HEIGHT,
                  MODEL_PICKER_CONTROL_SHAPE,
                  !unavailable && "disabled:opacity-100",
                  "w-full justify-between py-0 text-left aria-[pressed=true]:bg-[var(--color-surface-selected)] aria-[pressed=true]:text-primary focus-visible:ring-1 focus-visible:ring-accent",
                  selected
                    ? "bg-[var(--color-surface-selected)] text-primary"
                    : "text-secondary hover:bg-[var(--color-interactive-hover)] hover:text-primary",
                )}
                aria-pressed={selected}
                title={model.label}
                data-selected={selected ? "true" : undefined}
                data-testid={`model-option-${model.value}`}
              >
                <span className="min-w-0 flex-1 truncate">{model.label}</span>
                {planGrouped && model.free && (
                  <span
                    className="shrink-0 text-appkit-mini text-muted"
                    data-testid={`model-option-free-${model.value}`}
                  >
                    {t("free")}
                  </span>
                )}
                {selected && (
                  <Icons.Check className="h-3.5 w-3.5 shrink-0" aria-hidden />
                )}
              </Button>
            </li>
          </Fragment>
        );
      })}
      {showMore && (
        <li>
          <Button
            variant="ghost"
            size="sm"
            type="button"
            onClick={() => setExpanded(true)}
            aria-expanded={expanded}
            className={cn(
              menuItemVariants(),
              MODEL_PICKER_CONTROL_HEIGHT,
              MODEL_PICKER_CONTROL_SHAPE,
              "w-full justify-start py-0 text-muted hover:text-primary",
            )}
            data-testid="model-picker-more"
          >
            {t("more")}
          </Button>
        </li>
      )}
      {visible.length === 0 && (
        <li className="px-appkit-sm py-appkit-sm text-center text-appkit-small text-muted">
          {t("noModelsMatch")}
        </li>
      )}
    </ul>
  );
}
